/**
 * pending_channels Collection Schema
 *
 * This file defines the TypeScript interfaces for the pending_channels Firestore collection.
 * The schema uses a denormalized, flat structure following Firebase best practices.
 */

/**
 * Channel Data Fields
 * Basic information about the channel
 */
export interface ChannelData {
  /** Unique identifier for the channel (e.g., YouTube channel ID) */
  id: string;
  /** Channel name */
  name: string;
  /** Channel Title */
  title: string;
  /** URL to the channel */
  url: string;
  /** Channel description or summary */
  description: string;
}

/**
 * Channel Metrics
 * Quantitative metrics about the channel
 */
export interface ChannelMetrics {
  /** Number of subscribers/followers */
  subscriber_count: number;
  /** Number of videos (for video platforms) */
  video_count: number;
  /** Total number of views */
  view_count: number;
  /** Number of playlists */
  playlist_count: number;
}

/**
 * Contact Information
 * Ways to contact the channel owner
 */
export interface ContactInformation {
  /** Email address of the channel owner */
  email: string;
  /** Social media handles/URLs (JSON string or space-separated) */
  social_media: string;
  /** Website URL */
  website: string;
}

/**
 * Social Links
 * Structured social media links for the channel
 */
export interface SocialLinks {
  /** Twitter/X profile URL */
  twitter: string | null;
  /** Instagram profile URL */
  instagram: string | null;
  /** Facebook profile URL */
  facebook: string | null;
  /** Website URL */
  website: string | null;
}

/**
 * Discovery Metadata
 * Information about how the channel was discovered
 */
export interface DiscoveryMetadata {
  /** How the channel was discovered (e.g., "search", "referral", "manual") */
  method: string;
  /** Date when the channel was discovered (Firestore Timestamp) */
  date: any; // Firebase ServerValue.TIMESTAMP
  /** Search query used to discover the channel (if applicable) */
  query: string;
  /** Reason for discovery/interest */
  reason: string;
}

/**
 * Workflow Metadata
 * Tracks the channel's status in the outreach workflow
 */
export interface WorkflowMetadata {
  /** Current status of the channel (e.g., "pending", "approved", "rejected", "contacted") */
  status: string;
  /** Timestamp when the record was created (Firestore Timestamp) */
  added_at: any; // Firebase ServerValue.TIMESTAMP
  /** User who added the record (UID or email) */
  added_by: string;
  /** Timestamp when the record was last updated (Firestore Timestamp) */
  updated_at: any; // Firebase ServerValue.TIMESTAMP
  /** User who last updated the record (UID or email) */
  updated_by: string;
}

/**
 * Outreach Tracking
 * Tracks outreach attempts and communications
 */
export interface OutreachTracking {
  /** Status of outreach efforts (e.g., "pending", "contacted", "responded", "not_interested") */
  status: string;
  /** Date of the last outreach attempt (Firestore Timestamp) */
  date: any; // Firebase ServerValue.TIMESTAMP
  /** Notes about outreach attempts, responses, or communication */
  notes: string;
}

/**
 * Approval Metadata
 * Tracks approval workflow and final disposition
 */
export interface ApprovalMetadata {
  /** User who approved the channel (UID or email) */
  approver: string;
  /** Timestamp of approval (Firestore Timestamp) */
  timestamp: any; // Firebase ServerValue.TIMESTAMP
  /** Target table where the channel will be moved after approval */
  target_table: string;
}

/**
 * Main PendingChannel Document
 * Complete schema for a channel in the pending_channels collection
 */
export interface PendingChannel {
  // Channel Data
  id: string;
  title: string;
  url: string;
  description: string;

  // Metrics
  subscriber_count: number;
  video_count: number;
  view_count: number;
  playlist_count: number;

  // Contact Information
  email: string;
  social_media: string;
  website: string;

  // Discovery Metadata
  discovery_method: string;
  discovery_date: any; // Firebase ServerValue.TIMESTAMP
  discovery_query: string;
  discovery_reason: string;

  // Workflow Metadata
  workflow_status: string;
  added_at: any; // Firebase ServerValue.TIMESTAMP
  added_by: string;
  updated_at: any; // Firebase ServerValue.TIMESTAMP
  updated_by: string;

  // Outreach Tracking
  outreach_status: string;
  outreach_date: any; // Firebase ServerValue.TIMESTAMP
  outreach_notes: string;

  // Approval Metadata
  approval_approver: string;
  approval_timestamp: any; // Firebase ServerValue.TIMESTAMP
  approval_target_table: string;

  // Rejection Metadata
  rejection_reason: string;
  rejection_notes: string;
  rejected_at: any; // Firebase ServerValue.TIMESTAMP
  rejected_by: string;

  // General Notes
  notes: string;

  // Firestore Document Metadata
  /** Auto-generated document ID */
  _id?: string;

  // Discovery Optimization Fields (New)
  /** Source of discovery (curator_discovery, manual, or import) */
  discoverySource?: DiscoverySource;
  /** Primary contact email for the channel */
  contactEmail?: string | null;
  /** Structured social media links */
  socialLinks?: SocialLinks;
  /** Timestamp when the channel was discovered */
  discoveredAt?: any; // Firebase ServerValue.TIMESTAMP
  /** Timestamp when the channel data was last validated */
  lastValidated?: any; // Firebase ServerValue.TIMESTAMP
  /** Whether the channel has been pre-qualified */
  preQualified?: boolean;
}

/**
 * Status Enums for common status fields
 */
export const WORKFLOW_STATUS = {
  PENDING: "pending",
  REVIEW: "review",
  APPROVED: "approved",
  REJECTED: "rejected",
  CONTACTED: "contacted",
} as const;

export const OUTREACH_STATUS = {
  PENDING: "pending",
  CONTACTED: "contacted",
  RESPONDED: "responded",
  NOT_INTERESTED: "not_interested",
  CONVERTED: "converted",
} as const;

export const DISCOVERY_METHODS = {
  CURATOR_DISCOVERY: "curator_discovery",
  SEARCH: "search",
  REFERRAL: "referral",
  MANUAL: "manual",
  IMPORT: "import",
  API: "api",
} as const;

export type DiscoverySource = "curator_discovery" | "manual" | "import";

/**
 * Rejection Reason Enum
 * Reasons for rejecting a pending channel
 */
export const REJECTION_REASONS = {
  NOT_RELEVANT: "not_relevant",
  DUPLICATE: "duplicate",
  INVALID_CHANNEL: "invalid_channel",
  LOW_QUALITY: "low_quality",
  OTHER: "other",
} as const;

export type RejectionReason =
  (typeof REJECTION_REASONS)[keyof typeof REJECTION_REASONS];

/**
 * Validation utilities for PendingChannel documents
 */
export class PendingChannelValidator {
  /**
   * Validates a PendingChannel object
   * @param channel The channel object to validate
   * @returns Array of validation errors (empty if valid)
   */
  static validate(channel: Partial<PendingChannel>): string[] {
    const errors: string[] = [];

    // Required fields
    if (
      !channel.id ||
      typeof channel.id !== "string" ||
      channel.id.trim() === ""
    ) {
      errors.push("id is required and must be a non-empty string");
    }

    if (
      !channel.title ||
      typeof channel.title !== "string" ||
      channel.title.trim() === ""
    ) {
      errors.push("title is required and must be a non-empty string");
    }

    if (
      !channel.url ||
      typeof channel.url !== "string" ||
      channel.url.trim() === ""
    ) {
      errors.push("url is required and must be a non-empty string");
    }

    // Email validation
    if (channel.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(channel.email)) {
        errors.push("email must be a valid email address");
      }
    }

    // Numeric fields must be non-negative
    const numericFields = [
      "subscriber_count",
      "video_count",
      "view_count",
      "playlist_count",
    ];

    numericFields.forEach((field) => {
      const value = channel[field as keyof PendingChannel] as any;
      if (value !== undefined && value !== null) {
        if (typeof value !== "number" || value < 0) {
          errors.push(`${field} must be a non-negative number`);
        }
      }
    });

    // Status fields validation
    if (channel.workflow_status) {
      const validStatuses = Object.values(WORKFLOW_STATUS);
      if (!validStatuses.includes(channel.workflow_status as any)) {
        errors.push(
          `workflow_status must be one of: ${validStatuses.join(", ")}`,
        );
      }
    }

    if (channel.outreach_status) {
      const validStatuses = Object.values(OUTREACH_STATUS);
      if (!validStatuses.includes(channel.outreach_status as any)) {
        errors.push(
          `outreach_status must be one of: ${validStatuses.join(", ")}`,
        );
      }
    }

    if (channel.discovery_method) {
      const validMethods = Object.values(DISCOVERY_METHODS);
      if (!validMethods.includes(channel.discovery_method as any)) {
        errors.push(
          `discovery_method must be one of: ${validMethods.join(", ")}`,
        );
      }
    }

    // Rejection reason validation
    if (channel.rejection_reason) {
      const validReasons = Object.values(REJECTION_REASONS);
      if (!validReasons.includes(channel.rejection_reason as any)) {
        errors.push(
          `rejection_reason must be one of: ${validReasons.join(", ")}`,
        );
      }
      // If reason is 'other', notes must be provided
      if (channel.rejection_reason === REJECTION_REASONS.OTHER) {
        if (!channel.rejection_notes || channel.rejection_notes.trim() === "") {
          errors.push(
            'rejection_notes is required when rejection_reason is "other"',
          );
        }
      }
    }

    // New field validations for Discovery Optimization
    // discoverySource validation
    if (channel.discoverySource) {
      const validSources: DiscoverySource[] = [
        "curator_discovery",
        "manual",
        "import",
      ];
      if (!validSources.includes(channel.discoverySource)) {
        errors.push(
          `discoverySource must be one of: ${validSources.join(", ")}`,
        );
      }
    }

    // contactEmail validation (similar to email)
    if (channel.contactEmail !== undefined && channel.contactEmail !== null) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (
        typeof channel.contactEmail === "string" &&
        channel.contactEmail.trim() !== "" &&
        !emailRegex.test(channel.contactEmail)
      ) {
        errors.push("contactEmail must be a valid email address");
      }
    }

    // preQualified validation
    if (
      channel.preQualified !== undefined &&
      typeof channel.preQualified !== "boolean"
    ) {
      errors.push("preQualified must be a boolean");
    }

    // socialLinks validation
    if (channel.socialLinks) {
      const urlRegex =
        /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
      const socialFields: (keyof SocialLinks)[] = [
        "twitter",
        "instagram",
        "facebook",
        "website",
      ];

      socialFields.forEach((field) => {
        const value = channel.socialLinks![field];
        if (
          value !== null &&
          value !== undefined &&
          typeof value === "string" &&
          value.trim() !== ""
        ) {
          if (!urlRegex.test(value)) {
            errors.push(`socialLinks.${field} must be a valid URL`);
          }
        }
      });
    }

    return errors;
  }

  /**
   * Validates a PendingChannel object and throws if invalid
   * @param channel The channel object to validate
   * @throws Error if validation fails
   */
  static validateOrThrow(channel: Partial<PendingChannel>): void {
    const errors = this.validate(channel);
    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join("; ")}`);
    }
  }

  /**
   * Check if a channel is valid
   * @param channel The channel object to validate
   * @returns true if valid, false otherwise
   */
  static isValid(channel: Partial<PendingChannel>): boolean {
    return this.validate(channel).length === 0;
  }
}

/**
 * Default values for creating new pending channel documents
 */
export const getDefaultPendingChannel = (
  partial: Partial<PendingChannel> = {},
): Partial<PendingChannel> => ({
  subscriber_count: 0,
  video_count: 0,
  view_count: 0,
  playlist_count: 0,
  discovery_method: DISCOVERY_METHODS.MANUAL,
  workflow_status: WORKFLOW_STATUS.PENDING,
  outreach_status: OUTREACH_STATUS.PENDING,
  email: "",
  social_media: "",
  website: "",
  description: "",
  discovery_query: "",
  discovery_reason: "",
  outreach_notes: "",
  approval_approver: "",
  approval_target_table: "",
  rejection_reason: "",
  rejection_notes: "",
  rejected_by: "",
  notes: "",
  // New Discovery Optimization Fields
  discoverySource: DISCOVERY_METHODS.MANUAL as DiscoverySource,
  contactEmail: null,
  socialLinks: {
    twitter: null,
    instagram: null,
    facebook: null,
    website: null,
  },
  preQualified: true, // Assume existing channels are qualified
  ...partial,
});
